import { Button } from '@crowdex/ui/components/button';
import { Cloud, FolderGit2, FolderOpen, Laptop, MonitorDown } from 'lucide-react';
import { NoticeBanner } from '@/components/desktop-shell/error-banner';
import { browserDesktopOs } from '@/lib/desktop-downloads';
import type { CloudProjectMissingActions } from '@/lib/desktop-shell/cloud-start-recovery';
import { settingsSectionPath } from '@/lib/settings-page/sections';
import { navigateApp } from '@/vite/navigation-store';

export type { CloudProjectMissingActions };

const ACTION = 'h-8 rounded-control px-3.5';

/**
 * The cloud start was refused: Crowdex Cloud keeps no copy of the project. Rather than the error,
 * the ways on: its folder on this computer (Continue on this computer), another folder linked to
 * it, the forge's copy in the cloud; off a computer, another repository or the desktop app.
 */
export function CloudProjectMissingNotice({
  actions,
  layout,
}: {
  actions: CloudProjectMissingActions;
  layout?: { compact?: boolean; className?: string };
}) {
  const { recovery, local } = actions;
  const { localPath } = recovery;
  const cloudCopy = recovery.forge !== null && (
    <Button className={ACTION} onClick={actions.onUseCloudCopy} size="sm" variant="outline">
      <Cloud aria-hidden className="size-3.5" /> Use a cloud copy
    </Button>
  );
  const buttons = local ? (
    <>
      {cloudCopy}
      <Button
        className={ACTION}
        onClick={actions.onChooseFolder}
        size="sm"
        variant={localPath === null ? 'default' : 'outline'}
      >
        <FolderOpen aria-hidden className="size-3.5" /> Choose a folder…
      </Button>
      {localPath !== null && (
        <Button className={ACTION} onClick={() => actions.onContinueHere(localPath)} size="sm">
          <Laptop aria-hidden className="size-3.5" /> Continue on this computer
        </Button>
      )}
    </>
  ) : (
    <>
      {browserDesktopOs() !== null && (
        <Button
          className={ACTION}
          onClick={() => navigateApp(settingsSectionPath('general'))}
          size="sm"
          variant="outline"
        >
          <MonitorDown aria-hidden className="size-3.5" /> Use the desktop app
        </Button>
      )}
      {cloudCopy}
      <Button className={ACTION} onClick={actions.onChooseRepository} size="sm">
        <FolderGit2 aria-hidden className="size-3.5" /> Choose a repository
      </Button>
    </>
  );
  return (
    <NoticeBanner
      {...layout}
      actions={buttons}
      title={`${recovery.name} isn't in Crowdex Cloud yet.`}
      tone="warning"
    >
      {local && localPath !== null ? 'Its folder is on this computer.' : undefined}
    </NoticeBanner>
  );
}
